/**
 * SSO settings — /w/:slug/app/settings/sso.
 *
 * PR-P0-4 · ADR-0015 frontend addition: adds "Enforce SSO for this
 * workspace" enable/disable toggles alongside the existing WorkOS
 * Admin Portal launch button.
 *
 * Two related concepts, both surfaced here:
 *
 *   1. **WorkOS Organization provisioning** — click "Configure SSO"
 *      to open the Admin Portal in a new tab. Provisions the WorkOS
 *      Organization if needed; admin picks / configures IdP there.
 *      Unchanged from the pre-P0-4 version.
 *
 *   2. **Enforcement** — click "Enforce SSO for this workspace" to
 *      set `workspaces.self_serve_disabled = 1`. New sign-ups with a
 *      matching primary_domain get routed to the workspace's WorkOS
 *      Organization instead of AuthKit. Disable requires typing
 *      `DISABLE_SSO` into a confirmation field.
 *
 * The two are distinct on purpose (ADR-0015): a workspace can provision
 * SSO to poke around WorkOS UI without committing to enforcement, and
 * enforcement can be turned off without losing the WorkOS config.
 */

import { useCallback, useEffect, useState, useId } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button } from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { req, ApiError } from '../lib/api';

interface SsoStatus {
  eligible: boolean;
  tier: 'none' | 'oidc' | 'saml_scim';
  organization_provisioned: boolean;
  workos_organization_id: string | null;
  /** ADR-0015 enforcement flag — echoed by GET /settings/sso. */
  self_serve_disabled?: 0 | 1;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; status: SsoStatus }
  | { kind: 'error'; message: string };

export function SsoSettings() {
  const { workspace, role } = useWorkspace();
  const isOwner = role === 'owner';
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);

  const reload = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const body = await req<SsoStatus>(
        `/api/w/${encodeURIComponent(workspace.slug)}/settings/sso`
      );
      setState({ kind: 'ready', status: body });
    } catch (e) {
      setState({
        kind: 'error',
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }, [workspace.slug]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openPortal = useCallback(
    async (intent: 'sso' | 'dsync') => {
      setBusy(true);
      setNotice(null);
      try {
        const returnUrl = `${window.location.origin}/w/${encodeURIComponent(
          workspace.slug
        )}/app/settings/sso?portal=returned`;
        const body = await req<{ portal_url?: string }>(
          `/api/w/${encodeURIComponent(workspace.slug)}/settings/sso/portal-link`,
          { method: 'POST', body: JSON.stringify({ return_url: returnUrl, intent }) }
        );
        if (!body.portal_url) {
          setNotice("Couldn't open portal — no URL returned.");
          return;
        }
        window.open(body.portal_url, '_blank', 'noopener,noreferrer');
        await reload();
      } catch (e) {
        setNotice(e instanceof ApiError ? e.message : "Couldn't open portal.");
      } finally {
        setBusy(false);
      }
    },
    [workspace.slug, reload]
  );

  const enableEnforcement = useCallback(async () => {
    setBusy(true);
    setNotice(null);
    try {
      const returnUrl = `${window.location.origin}/w/${encodeURIComponent(
        workspace.slug
      )}/app/settings/sso?portal=returned`;
      const body = await req<{ portal_url?: string }>(
        `/api/w/${encodeURIComponent(workspace.slug)}/settings/sso/enable`,
        { method: 'POST', body: JSON.stringify({ return_url: returnUrl }) }
      );
      // On enable, the server also generated a fresh portal URL —
      // open it so the admin lands straight in WorkOS UI to finish
      // wiring their IdP connection.
      if (body.portal_url) {
        window.open(body.portal_url, '_blank', 'noopener,noreferrer');
      }
      setNotice('SSO enforcement enabled. New sign-ups on your domain now route through your identity provider.');
      await reload();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : "Couldn't enable SSO.");
    } finally {
      setBusy(false);
    }
  }, [workspace.slug, reload]);

  const disableEnforcement = useCallback(
    async (confirmValue: string) => {
      setBusy(true);
      setNotice(null);
      try {
        await req<{ detail?: string }>(
          `/api/w/${encodeURIComponent(workspace.slug)}/settings/sso/disable`,
          { method: 'POST', body: JSON.stringify({ confirm: confirmValue }) }
        );
        setNotice('SSO enforcement disabled. Existing sessions unaffected; new sign-ups fall back to self-serve.');
        setDisableDialogOpen(false);
        await reload();
      } catch (e) {
        setNotice(e instanceof ApiError ? e.message : "Couldn't disable SSO.");
      } finally {
        setBusy(false);
      }
    },
    [workspace.slug, reload]
  );

  if (state.kind === 'loading') {
    return (
      <div style={shell} aria-busy="true">
        Loading SSO settings…
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div style={shell}>
        <h1 style={{ marginTop: 0 }}>SSO</h1>
        <p style={{ color: 'var(--band-red)' }}>{state.message}</p>
      </div>
    );
  }

  const s = state.status;
  const enforced = s.self_serve_disabled === 1;

  return (
    <div style={shell}>
      <h1 style={{ marginTop: 0 }}>Single Sign-On (SSO)</h1>

      {notice ? (
        <Alert variant={notice.startsWith("Couldn't") ? 'error' : 'info'}>
          {notice}
        </Alert>
      ) : null}

      {!s.eligible ? (
        <UpsellCard workspaceSlug={workspace.slug} />
      ) : (
        <>
          <StatusCard
            status={s}
            isOwner={isOwner}
            busy={busy}
            onOpenPortal={openPortal}
          />
          <EnforcementCard
            enforced={enforced}
            provisioned={s.organization_provisioned}
            isOwner={isOwner}
            busy={busy}
            onEnable={enableEnforcement}
            onDisable={() => setDisableDialogOpen(true)}
          />
        </>
      )}

      <HelpCard />

      {disableDialogOpen ? (
        <DisableConfirmDialog
          onCancel={() => setDisableDialogOpen(false)}
          onConfirm={disableEnforcement}
          busy={busy}
        />
      ) : null}
    </div>
  );
}

// ---- Upsell card (Free / no-SSO plan) --------------------------------

function UpsellCard({ workspaceSlug }: { workspaceSlug: string }) {
  return (
    <section
      aria-label="Upgrade for SSO"
      style={{
        border: '1px solid var(--border-strong)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-5)',
        background: 'var(--surface-elevated)',
      }}
    >
      <h2 style={{ marginTop: 0 }}>SSO requires Growth or Enterprise</h2>
      <p style={{ color: 'var(--text-muted)' }}>
        SAML / OIDC SSO lets your team sign in through Google Workspace,
        Microsoft Entra, Okta, or any SAML IdP. Directory Sync (Enterprise
        only) automatically deprovisions users when they leave your IdP.
      </p>
      <Link
        to={`/w/${encodeURIComponent(workspaceSlug)}/app/settings/billing`}
        style={{
          color: 'var(--accent)',
          fontWeight: 500,
          textDecoration: 'none',
        }}
      >
        View plans &rsaquo;
      </Link>
    </section>
  );
}

// ---- Status card ------------------------------------------------------

function StatusCard({
  status,
  isOwner,
  busy,
  onOpenPortal,
}: {
  status: SsoStatus;
  isOwner: boolean;
  busy: boolean;
  onOpenPortal: (intent: 'sso' | 'dsync') => void;
}) {
  const provisioned = status.organization_provisioned;
  return (
    <section
      aria-label="SSO status"
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-5)',
        background: 'var(--surface-elevated)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--sp-3)',
        }}
      >
        <h2 style={{ margin: 0 }}>
          {provisioned ? 'WorkOS Organization ready' : 'Not configured'}
        </h2>
        <span
          style={{
            fontSize: 'var(--fs-xs)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: provisioned ? 'var(--band-green)' : 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          {provisioned ? 'PROVISIONED' : 'NOT PROVISIONED'}
        </span>
      </div>
      <p style={{ color: 'var(--text-muted)' }}>
        {provisioned
          ? 'Your workspace has a WorkOS Organization. Click "Manage SSO connection" to add or update a Connection (Google Workspace, Microsoft Entra, Okta, SAML, etc.).'
          : "Click \"Configure SSO\" to open WorkOS's hosted setup UI in a new tab. You'll pick your identity provider and enter the details there — no need to leave the workspace."}
      </p>

      {!isOwner ? (
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
          Only the workspace Owner can configure SSO.
        </p>
      ) : (
        <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
          <Button variant="primary" onClick={() => onOpenPortal('sso')} disabled={busy}>
            {busy ? 'Opening…' : provisioned ? 'Manage SSO connection' : 'Configure SSO'}
          </Button>
          {status.tier === 'saml_scim' ? (
            <Button variant="ghost" onClick={() => onOpenPortal('dsync')} disabled={busy}>
              Configure Directory Sync
            </Button>
          ) : null}
        </div>
      )}
    </section>
  );
}

// ---- Enforcement card (ADR-0015) --------------------------------------

function EnforcementCard({
  enforced,
  provisioned,
  isOwner,
  busy,
  onEnable,
  onDisable,
}: {
  enforced: boolean;
  provisioned: boolean;
  isOwner: boolean;
  busy: boolean;
  onEnable: () => void;
  onDisable: () => void;
}) {
  return (
    <section
      aria-label="SSO enforcement"
      style={{
        border: `1px solid ${enforced ? 'var(--band-green-mark)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-5)',
        background: enforced ? 'var(--band-green-bg)' : 'var(--surface-elevated)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--sp-3)',
        }}
      >
        <h2 style={{ margin: 0 }}>
          {enforced ? 'SSO enforced' : 'Enforcement not on'}
        </h2>
        <span
          style={{
            fontSize: 'var(--fs-xs)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: enforced ? 'var(--band-green)' : 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          {enforced ? 'ENFORCED' : 'OPTIONAL'}
        </span>
      </div>
      <p style={{ color: 'var(--text-muted)' }}>
        {enforced
          ? 'New sign-ups from your workspace domain are routed through your identity provider. Existing sessions are unaffected.'
          : 'Enforcement is off. Anyone matching your workspace domain can still sign up via self-serve. Turn on to require IdP-mediated sign-in for new users.'}
      </p>
      {!isOwner ? (
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
          Only the workspace Owner can change enforcement.
        </p>
      ) : enforced ? (
        <Button variant="danger" onClick={onDisable} disabled={busy}>
          Disable enforcement
        </Button>
      ) : (
        <>
          <Button
            variant="primary"
            onClick={onEnable}
            disabled={busy || !provisioned}
          >
            {busy ? 'Enabling…' : 'Enforce SSO for this workspace'}
          </Button>
          {!provisioned ? (
            <p
              style={{
                marginTop: 'var(--sp-2)',
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-muted)',
              }}
            >
              Configure SSO first (above) to provision the WorkOS Organization,
              then return here to enforce.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

// ---- Disable-typed-confirm dialog -------------------------------------

const DISABLE_CONFIRM_LITERAL = 'DISABLE_SSO';

function DisableConfirmDialog({
  onCancel,
  onConfirm,
  busy,
}: {
  onCancel: () => void;
  onConfirm: (confirm: string) => void;
  busy: boolean;
}) {
  const [value, setValue] = useState('');
  const matches = value === DISABLE_CONFIRM_LITERAL;
  const inputId = useId();
  return (
    <div
      // ADR-1.1: destructive-confirm uses `alertdialog` (WAI-ARIA
      // authoring practice) — the user MUST acknowledge before
      // continuing. This also sidesteps C10 which is about
      // non-alert dialog semantics on mobile-only surfaces.
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="disable-sso-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 18, 25, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'var(--sp-4)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: 'var(--surface-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--sp-5)',
          maxWidth: 480,
          width: '100%',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <h2 id="disable-sso-title" style={{ margin: '0 0 var(--sp-3)' }}>
          Disable SSO enforcement?
        </h2>
        <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>
          New sign-ups on your workspace domain will fall back to self-serve
          AuthKit. Existing member sessions and your WorkOS Organization
          configuration are preserved — you can re-enable enforcement with
          one click later.
        </p>
        <label
          htmlFor={inputId}
          style={{
            display: 'block',
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-muted)',
            marginBottom: 'var(--sp-2)',
          }}
        >
          Type <code style={{ fontFamily: 'var(--font-mono)' }}>{DISABLE_CONFIRM_LITERAL}</code> to confirm:
        </label>
        <input
          id={inputId}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          style={{
            width: '100%',
            padding: '8px var(--sp-3)',
            background: 'var(--surface-elevated)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text)',
            fontFamily: 'inherit',
            fontSize: 'var(--fs-md)',
            minHeight: 36,
            marginBottom: 'var(--sp-3)',
          }}
        />
        <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => onConfirm(value)}
            disabled={busy || !matches}
          >
            {busy ? 'Disabling…' : 'Disable enforcement'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- Help card --------------------------------------------------------

function HelpCard() {
  return (
    <section
      aria-label="SSO help"
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-4) var(--sp-5)',
        background: 'var(--surface-alt)',
      }}
    >
      <h3 style={{ marginTop: 0, fontSize: 'var(--fs-md)' }}>What happens next</h3>
      <ol style={{ color: 'var(--text-muted)', margin: '0 0 var(--sp-2) var(--sp-5)' }}>
        <li>You'll be redirected to a WorkOS-hosted setup page in a new tab.</li>
        <li>Pick your identity provider and follow the guided steps.</li>
        <li>
          When done, WorkOS bounces you back here. Turn on enforcement to
          require IdP sign-in for new users on your domain.
        </li>
      </ol>
      <p style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)', margin: 0 }}>
        Existing members keep access regardless of enforcement. The flag
        gates NEW sign-ups only, at OAuth initiation.
      </p>
    </section>
  );
}

// ---- Style tokens ----------------------------------------------------

const shell: React.CSSProperties = {
  maxWidth: 720,
  margin: '0 auto',
  padding: 'var(--sp-6)',
  display: 'grid',
  gap: 'var(--sp-4)',
  fontFamily: 'inherit',
};
